/**
 * `<ContactEditRoute>` — `/w/:slug/contacts/:contactId/edit`.
 *
 * Dedicated edit page (mirrors the Agents editor) instead of a modal:
 * `<PageShell>` + the shared `<ContactProfileForm>` in edit mode. Owns the
 * update mutation; Cancel and a successful save both return to the contact
 * detail page.
 */

import { useWorkspace } from "@cometchat-ai/auth/client";
import { Alert, AlertDescription, Button, Skeleton, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { ContactProfileForm } from "../components/contacts/ContactProfileForm";
import { CheckTick, CloseX } from "../components/glyphs";
import { ChevronRight } from "../components/icons";
import { PageShell } from "../components/page-shell";
import { usePageBreadcrumb } from "../layouts/breadcrumb-trail";
import { ApiError } from "../lib/api/errors";
import { useContactsClient } from "../lib/api/use-contacts-client";
import type { Contact, ContactProfilePatch } from "../lib/api/types";

const FORM_ID = "contact-profile-form";

export function ContactEditRoute() {
  const { contactId } = useParams<{ contactId: string }>();
  const { t } = useTranslation("contacts");
  const { workspace } = useWorkspace();
  const client = useContactsClient();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const detailKey = ["contacts", "detail", contactId ?? ""] as const;
  const detailPath = `/w/${workspace.slug}/contacts/${contactId}`;

  const query = useQuery({
    queryKey: detailKey,
    queryFn: () => client.get(contactId!),
    enabled: Boolean(contactId),
    staleTime: 30_000,
  });

  const contact = query.data;
  const visibleName = contact ? (contact.displayName ?? contact.userId) : "";

  usePageBreadcrumb(
    contact
      ? [{ label: visibleName, to: detailPath }, { label: t("edit.crumb") }]
      : [{ label: t("edit.crumb") }],
  );

  const updateMutation = useMutation({
    mutationFn: (patch: ContactProfilePatch) => client.update(contactId!, patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<Contact>(detailKey);
      if (previous) qc.setQueryData<Contact>(detailKey, { ...previous, ...patch });
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
      toast.error(t("edit.error"));
    },
    onSuccess: () => {
      toast.success(t("edit.toast"));
      navigate(detailPath);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: detailKey });
      void qc.invalidateQueries({ queryKey: ["contacts", "list"] });
    },
  });

  if (query.isPending) {
    return (
      <PageShell className="max-w-2xl" data-testid="contact-edit-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }

  if (query.error || !contact) {
    const notFound = query.error instanceof ApiError && query.error.isNotFound();
    return (
      <PageShell className="max-w-2xl" data-testid="contact-edit-error">
        <Alert variant="destructive">
          <AlertDescription>
            {notFound ? t("detail.notFoundBody") : t("edit.error")}
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <Link to={`/w/${workspace.slug}/contacts`}>{t("detail.back")}</Link>
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell
      className="p-0"
      data-testid="contact-edit-route"
      title={
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(detailPath)}
            className="truncate text-base font-semibold tracking-[0.01em] text-foreground transition-colors hover:text-muted-foreground"
          >
            {visibleName}
          </button>
          <ChevronRight
            className="h-3.5 w-3.5 shrink-0 text-[color:var(--color-fg-subtle)]"
            aria-hidden="true"
          />
          <span className="truncate text-base font-medium tracking-[0.01em] text-muted-foreground">
            {t("edit.crumb")}
          </span>
        </div>
      }
    >
      <div className="overflow-auto px-6 py-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="rounded-2xl border border-border p-6">
            <ContactProfileForm
              id={FORM_ID}
              renderFooter={false}
              mode="edit"
              initial={{
                userId: contact.userId,
                displayName: contact.displayName ?? "",
                email: contact.email ?? "",
                phone: contact.phone ?? "",
                avatarUrl: contact.avatarUrl ?? "",
                locale: contact.locale ?? "",
                timezone: contact.timezone ?? "",
                metadata: JSON.stringify(contact.metadata ?? {}, null, 2),
              }}
              busy={updateMutation.isPending}
              onCancel={() => navigate(detailPath)}
              onSubmitEdit={(patch) => updateMutation.mutate(patch)}
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => navigate(detailPath)}
              disabled={updateMutation.isPending}
              className="h-8 min-w-[132px] justify-center gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
              data-testid="contact-edit-cancel"
            >
              <CloseX className="h-3.5 w-3.5" />
              {t("form.cancel")}
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              size="sm"
              disabled={updateMutation.isPending}
              className="h-8 min-w-[132px] justify-center gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
              data-testid="contact-edit-submit"
            >
              <CheckTick className="h-3.5 w-3.5" />
              {t("form.save")}
            </Button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
